import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  computed,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { TranslateService, TranslatePipe } from '@ngx-translate/core';

import { injectUseCaseTemplatesQuery } from 'src/app/api/queries/use-case.queries';
import { UseCaseTemplateSummary } from 'src/app/core/models/use-case.models';
import { TabComponent } from 'src/app/shared/utils/tab-manager';

/**
 * Step 1 (§12.1): template card gallery. Selecting a template drives the rest
 * of the wizard (schema, inputs). Exposes a FormGroup with a required
 * `template_id` so the tab manager treats the step as invalid until a template
 * is chosen (pattern: FeedTypeSelectorComponent).
 */
@Component({
  selector: 'app-use-case-template-selector',
  templateUrl: './use-case-template-selector.component.html',
  styleUrls: ['./use-case-template-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [TranslatePipe],
})
export class UseCaseTemplateSelectorComponent implements OnInit, TabComponent {
  @Output() templateSelected = new EventEmitter<UseCaseTemplateSummary>();

  private readonly translate = inject(TranslateService);

  readonly formGroup = new FormGroup({
    template_id: new FormControl<string | null>(null, Validators.required),
  });

  readonly templatesQuery = injectUseCaseTemplatesQuery();
  readonly templates = computed(
    () => this.templatesQuery.data()?.templates ?? [],
  );
  readonly isLoading = computed(() => this.templatesQuery.isLoading());
  readonly isError = computed(() => this.templatesQuery.isError());

  selectedId: string | null = null;

  ngOnInit(): void {}

  /**
   * Resolve a template's display title. A genuine translation of `title_i18n`
   * wins; otherwise the stored `title` wins; otherwise the `key` is humanized.
   * This repo's translate runtime echoes a MISSING key as its bare last
   * dot-segment, so a result equal to the full key OR that segment is treated
   * as "no translation", not a real value.
   *
   * @param template the template summary
   * @returns the display title
   */
  title(template: UseCaseTemplateSummary): string {
    const translated = this.translate.instant(template.title_i18n);
    const lastSegment =
      template.title_i18n.split('.').pop() ?? template.title_i18n;
    const isEchoedKey =
      translated === template.title_i18n || translated === lastSegment;

    let outcome: string;
    if (!isEchoedKey) {
      outcome = translated;
    } else if (template.title && template.title !== template.title_i18n) {
      outcome = template.title;
    } else {
      outcome = this.humanizeKey(template.key);
    }

    return outcome;
  }

  /**
   * Turn a snake_case template key into a Title Case label
   * (e.g. `brand_monitoring` -> `Brand Monitoring`).
   *
   * @param key the template key
   * @returns the humanized label
   */
  private humanizeKey(key: string): string {
    return key
      .split('_')
      .filter((word) => word.length > 0)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Select a template card, mark the step valid, and emit the choice.
   *
   * @param template the chosen template
   * @return void
   */
  select(template: UseCaseTemplateSummary): void {
    this.selectedId = template.id;
    this.formGroup.controls.template_id.setValue(template.id);
    this.formGroup.controls.template_id.markAsDirty();
    this.templateSelected.emit(template);
  }
}
